import React, { useState, useEffect } from 'react';
import { Sidebar } from './Dashboard';
import { API } from '../App';
import * as XLSX from 'xlsx';
import FloorOptions from './FloorOptions';

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f1a',
    display: 'flex',
  },
  main: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  pageTitle: {
    color: '#ffffff',
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  pageSubtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '40px',
  },
  card: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  cardSubtitle: {
    color: '#a0a0b0',
    fontSize: '13px',
    marginBottom: '24px',
  },
  settingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: '1px solid #0f3460',
  },
  settingLabel: {
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  settingDesc: {
    color: '#a0a0b0',
    fontSize: '13px',
  },
  toggleTrack: {
    width: '48px',
    height: '26px',
    borderRadius: '13px',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    position: 'relative',
    transition: 'background 0.2s',
  },
  numberInput: {
    width: '80px',
    padding: '8px 12px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '16px',
    outline: 'none',
    textAlign: 'center',
  },
  saveButton: {
    padding: '12px 32px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '24px',
  },
  success: {
    color: '#00cc00',
    fontSize: '14px',
    marginTop: '16px',
  },
  error: {
    color: '#e94560',
    fontSize: '14px',
    marginTop: '16px',
  },
  infoBox: {
    backgroundColor: '#0f3460',
    borderRadius: '10px',
    padding: '16px',
    marginTop: '16px',
  },
  infoText: {
    color: '#a0a0b0',
    fontSize: '13px',
    lineHeight: '1.6',
  },
  highlight: {
    color: '#e94560',
    fontWeight: 'bold',
  },
};

function Settings() {
  const token = localStorage.getItem('admin_token');
  const [sdEnabled, setSdEnabled] = useState(true);
  const [sdThreshold, setSdThreshold] = useState(3.5);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [backupDays, setBackupDays] = useState(30);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupMessage, setBackupMessage] = useState('');
  const [backupError, setBackupError] = useState('');

  const [gameTypes, setGameTypes] = useState([]);
  const [installLoading, setInstallLoading] = useState(false);
  const [installMessage, setInstallMessage] = useState('');
  const [installError, setInstallError] = useState('');

  useEffect(() => {
    loadSettings();
    loadGameTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadSettings() {
    const res = await fetch(`${API}/admin/settings`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    setSdEnabled(data.sd_filter_enabled);
    setSdThreshold(data.sd_threshold);
  }

  async function loadGameTypes() {
    try {
      const res = await fetch(`${API}/game-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGameTypes(data || []);
      }
    } catch (e) {
      // non-fatal — install card just won't render the "installed" state
    }
  }

  async function installBlackjackEz() {
    setInstallLoading(true);
    setInstallError('');
    setInstallMessage('');
    try {
      const res = await fetch(`${API}/admin/games/install-bj-ez`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }
      const data = await res.json();
      setInstallMessage(
        data.status === 'already_installed'
          ? 'Blackjack EZ is already installed.'
          : 'Blackjack EZ installed. It will appear in the mobile app on next session start.'
      );
      await loadGameTypes();
    } catch (e) {
      setInstallError(`Install failed: ${e.message}`);
    }
    setInstallLoading(false);
  }

  const bjEzInstalled = gameTypes.some(g => g.name === 'Blackjack EZ');

  async function runBackup() {
    setBackupLoading(true);
    setBackupError('');
    setBackupMessage('');
    try {
      const res = await fetch(`${API}/admin/backup?days=${backupDays}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(res.status === 429 ? 'Too many requests — try again in an hour' : `Server returned ${res.status}`);
      }
      const data = await res.json();

      const wb = XLSX.utils.book_new();

      const meta = [
        ['EZ Pace Data Backup'],
        [`Generated: ${new Date().toLocaleString()}`],
        [`Range: last ${data.days} days (since ${data.cutoff})`],
        [],
        ['Counts'],
        ['Sessions', data.counts.sessions],
        ['Rounds', data.counts.rounds],
        ['Time Codes', data.counts.time_codes],
        ['Dealers', data.counts.dealers],
        ['Game Types', data.game_types.length],
        ['Code Benchmarks', data.code_benchmarks.length],
        ['HPH Benchmarks', data.hph_benchmarks.length],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(meta), 'Metadata');

      const sheets = [
        ['Sessions', data.sessions],
        ['Rounds', data.rounds],
        ['TimeCodes', data.time_codes],
        ['Dealers', data.dealers],
        ['GameTypes', data.game_types],
        ['CodeBenchmarks', data.code_benchmarks],
        ['HphBenchmarks', data.hph_benchmarks],
      ];
      sheets.forEach(([name, rows]) => {
        const ws = rows.length > 0
          ? XLSX.utils.json_to_sheet(rows)
          : XLSX.utils.aoa_to_sheet([['(no rows)']]);
        XLSX.utils.book_append_sheet(wb, ws, name);
      });

      const suggestedName = `ezpace_backup_${data.days}d_${new Date().toISOString().split('T')[0]}.xlsx`;

      if ('showSaveFilePicker' in window) {
        try {
          const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
          const blob = new Blob([buf], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const handle = await window.showSaveFilePicker({
            suggestedName,
            types: [{
              description: 'Excel Workbook',
              accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
            }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          setBackupMessage(`Backup saved as ${handle.name} — ${data.counts.sessions} sessions, ${data.counts.rounds} rounds`);
          setBackupLoading(false);
          return;
        } catch (e) {
          if (e.name === 'AbortError') {
            setBackupMessage('Save cancelled');
            setBackupLoading(false);
            return;
          }
          // fall through to download fallback
        }
      }
      XLSX.writeFile(wb, suggestedName);
      setBackupMessage(`Backup downloaded — ${data.counts.sessions} sessions, ${data.counts.rounds} rounds`);
    } catch (e) {
      setBackupError(`Backup failed: ${e.message}`);
    }
    setBackupLoading(false);
  }

  async function saveSettings() {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${API}/admin/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sd_filter_enabled: sdEnabled,
          sd_threshold: parseFloat(sdThreshold),
        }),
      });
      if (res.ok) {
        setMessage('Settings saved successfully');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError('Failed to save settings');
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setLoading(false);
  }

  return (
    <div style={styles.container}>
      <Sidebar active="Settings" />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Settings</div>
        <div style={styles.pageSubtitle}>
          Configure system-wide settings for your casino account
        </div>

        <FloorOptions isAdmin={localStorage.getItem('admin_role') === 'admin'} />
        <FloorOptions isAdmin={localStorage.getItem('admin_role') === 'admin'} group="blackjack" />
        <FloorOptions isAdmin={localStorage.getItem('admin_role') === 'admin'} group="baccarat" />

        <div style={styles.card}>
          <div style={styles.cardTitle}>Outlier Filtering</div>
          <div style={styles.cardSubtitle}>
            Filters extreme data points from benchmark averages. Removes outliers caused by
            paused games, disputes, or observation errors before calculating averages.
          </div>

          <div style={styles.settingRow}>
            <div>
              <div style={styles.settingLabel}>Enable Outlier Filtering</div>
              <div style={styles.settingDesc}>
                Remove statistical outliers from benchmark averages
              </div>
            </div>
            <button
              style={{
                ...styles.toggleTrack,
                backgroundColor: sdEnabled ? '#e94560' : '#0f3460',
              }}
              onClick={() => setSdEnabled(!sdEnabled)}
            >
              <div style={{
                position: 'absolute',
                top: '3px',
                left: sdEnabled ? '25px' : '3px',
                width: '20px',
                height: '20px',
                backgroundColor: '#ffffff',
                borderRadius: '50%',
                transition: 'left 0.2s',
              }} />
            </button>
          </div>

          <div style={styles.settingRow}>
            <div>
              <div style={styles.settingLabel}>Threshold</div>
              <div style={styles.settingDesc}>
                Times further than this many standard deviations from the typical time
                are excluded. Default is 3.5 — lower values filter more aggressively.
              </div>
            </div>
            <input
              style={{
                ...styles.numberInput,
                opacity: sdEnabled ? 1 : 0.4,
              }}
              type="number"
              step="0.5"
              min="1"
              max="5"
              value={sdThreshold}
              onChange={e => setSdThreshold(e.target.value)}
              disabled={!sdEnabled}
            />
          </div>

          <div style={styles.infoBox}>
            <div style={styles.infoText}>
              <span style={styles.highlight}>How it works: </span>
              For each time code, the system finds the typical (median) time and how
              widely times normally spread around it, comparing times as ratios because a
              slow round can run much longer than a fast one can run short. Any round more than{' '}
              <span style={styles.highlight}>{sdThreshold} standard deviations</span>{' '}
              out is excluded from the benchmark average. A freak time cannot drag the typical
              time toward itself, so it is caught even in a small group of rounds, such as
              shuffles. The number of records used vs excluded is shown on the Benchmarks page.
            </div>
          </div>

          {message && <div style={styles.success}>{message}</div>}
          {error && <div style={styles.error}>{error}</div>}

          <button style={styles.saveButton} onClick={saveSettings} disabled={loading}>
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Data Backup</div>
          <div style={styles.cardSubtitle}>
            Download an Excel workbook with every available data point — sessions, rounds,
            individual time codes, dealers, game types, and benchmarks — for the selected
            window. Use this to archive, audit, or analyze outside EZ Pace.
          </div>

          <div style={styles.settingRow}>
            <div>
              <div style={styles.settingLabel}>Days of session data</div>
              <div style={styles.settingDesc}>
                Sessions started in the last 30, 60, or 90 days are included. Dealers,
                game types, and benchmarks are always exported as a current snapshot.
              </div>
            </div>
            <select
              style={{ ...styles.numberInput, width: '140px' }}
              value={backupDays}
              onChange={e => setBackupDays(parseInt(e.target.value, 10))}
              disabled={backupLoading}
            >
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>

          <div style={styles.infoBox}>
            <div style={styles.infoText}>
              {'showSaveFilePicker' in window ? (
                <>Your browser will open a <span style={styles.highlight}>Save As</span> dialog
                so you can pick the folder and filename. If you cancel, nothing is saved.</>
              ) : (
                <>Your browser doesn&apos;t support the Save As dialog, so the file will go to
                your default Downloads folder. To choose a folder, you can enable
                <span style={styles.highlight}> &quot;Always ask where to save each file&quot; </span>
                in your browser settings before clicking Download.</>
              )}
            </div>
          </div>

          {backupMessage && <div style={styles.success}>{backupMessage}</div>}
          {backupError && <div style={styles.error}>{backupError}</div>}

          <button style={styles.saveButton} onClick={runBackup} disabled={backupLoading}>
            {backupLoading ? 'Building backup…' : `Download ${backupDays}-Day Backup`}
          </button>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Game Types</div>
          <div style={styles.cardSubtitle}>
            Install additional game variants for your tenant. Installed games appear
            on the mobile Start Session screen and in the admin Benchmarks and Reports.
          </div>

          <div style={styles.settingRow}>
            <div>
              <div style={styles.settingLabel}>Blackjack EZ</div>
              <div style={styles.settingDesc}>
                Simplified two-timestamp Blackjack flow (Start Round → End Round).
                Belongs to the "All Blackjack" family.
              </div>
            </div>
            <button
              style={{
                ...styles.saveButton,
                marginTop: 0,
                opacity: bjEzInstalled || installLoading ? 0.5 : 1,
                cursor: bjEzInstalled || installLoading ? 'default' : 'pointer',
              }}
              onClick={installBlackjackEz}
              disabled={bjEzInstalled || installLoading}
            >
              {bjEzInstalled
                ? 'Installed'
                : installLoading
                  ? 'Installing…'
                  : 'Install Blackjack EZ'}
            </button>
          </div>

          {installMessage && <div style={styles.success}>{installMessage}</div>}
          {installError && <div style={styles.error}>{installError}</div>}
        </div>
      </div>
    </div>
  );
}

export default Settings;